// Rules for the consultation booking wizard's recovery states and for a reservation's payment
// state. Pure functions over the ReservationDto from core-service (status, charge, is_cancelled,
// reservation_time, payment: {is_paid}); the components decide what to render.

/** The steps after the reservation exists, in the order the wizard runs them. */
export const STEPS = Object.freeze({answers: 'answers', payment: 'payment', confirm: 'confirm'});

/** Copy for a booking that stopped at `step`: the reservation is kept, the step is not done. */
export const PARTIAL_COPY = Object.freeze({
  [STEPS.answers]: {
    heading: 'Your consultation is reserved, but your answers were not saved',
    body: 'Your time is held for you. Retry to save your intake answers.',
  },
  [STEPS.payment]: {
    heading: 'Your consultation is reserved but not paid yet',
    body: 'Your time is held for you, but no payment has been taken. Retry to continue to payment.',
  },
  [STEPS.confirm]: {
    heading: 'We could not confirm your payment',
    body: 'Your card may already have been charged. Retry to confirm it; retrying does not charge you again.',
  },
});

/**
 * Cancel is offered only before the payment is confirmed. After a failed confirmation the card
 * may already be charged, and cancelling then would leave it unrefunded if the server still
 * records the payment as unpaid.
 */
export const canCancelAt = (step) => step !== STEPS.confirm;

/** Confirmed, not cancelled, fee above zero, and not paid yet. */
export const needsPayment = (reservation) =>
  Boolean(reservation) &&
  reservation.status === 'confirmed' &&
  !reservation.is_cancelled &&
  (reservation.charge || 0) > 0 &&
  !(reservation.payment && reservation.payment.is_paid);

/** Payment can still be completed from the record: it is owed and the consultation has not started. */
export const paymentDue = (reservation, now = Date.now()) =>
  needsPayment(reservation) && Date.parse(reservation.reservation_time) > now;

/** Copy once a confirmed consultation was released (unpaid) and the payment then arrived. */
export const RELEASED_COPY = Object.freeze({
  heading: 'Consultation released',
  body: 'This consultation was released before your payment arrived. Your payment has been refunded.',
});

/** Copy for a consultation released because it was not paid in time. */
export const RELEASED_UNPAID_COPY =
  'This consultation was not paid in time, so it was released. The time is no longer held for you.';

/**
 * Payment confirmation answers 409 when the payment landed after the consultation was released.
 * The charge is refunded by then, so the caller shows RELEASED_COPY instead of a retry.
 */
export const isReleasedConflict = (err) => Boolean(err && err.response && err.response.status === 409);

/** Released by the server: not held, not payable, not cancellable. */
export const isReleased = (reservation) => Boolean(reservation) && reservation.status === 'released';

/** Status label shown on a card. Only released rows carry one; other statuses keep their current UI. */
export const statusLabel = (reservation) => (isReleased(reservation) ? 'Released' : null);

/**
 * Pay-by deadline (ISO string) for a consultation the patient still owes, or null. The server
 * sets `expires_at` to the pay-by time for these rows; free and unconfirmed rows carry none.
 */
export const payByDeadline = (reservation, now = Date.now()) =>
  needsPayment(reservation) && reservation.expires_at && paymentDue(reservation, now)
    ? reservation.expires_at
    : null;
